const RULER_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>';

// Measurements outside these are treated as typos
const HEIGHT_LIMITS = [100, 230];
const WEIGHT_LIMITS = [20, 200];
// How far outside the chart a measurement may be and still get the smallest
// or largest size. More slack below, where the jersey is only a little loose,
// than above, where it would be tight
const TOLERANCE = {
  height: { below: 5, above: 3 },
  weight: { below: 5, above: 5 },
};
const CM_PER_INCH = 2.54;
const KG_PER_LB = 0.45359237;
const UNIT_STORAGE_KEY = "fit-finder-unit";
const MEASUREMENTS_STORAGE_KEY = "fit-finder-measurements";

// Storage can be blocked; the finder then simply starts empty each time
function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    // Nothing to do
  }
}

class FitFinder extends HTMLElement {
  connectedCallback() {
    this.section = this.closest(".main-product-section") || document.body;
    this.dialog = this.querySelector("dialog");
    this.inputs = {};
    this.querySelectorAll("[data-fit-finder-input]").forEach((input) => {
      this.inputs[input.dataset.fitFinderInput] = input;
    });
    this.emptyText = this.querySelector("[data-fit-finder-empty]");
    this.output = this.querySelector("[data-fit-finder-output]");
    this.sizeText = this.querySelector("[data-fit-finder-size]");
    this.noteText = this.querySelector("[data-fit-finder-note]");
    this.selectButton = this.querySelector("[data-fit-finder-select]");
    this.selectSize = this.querySelector("[data-fit-finder-select-size]");
    this.outsideBox = this.querySelector("[data-fit-finder-outside]");
    this.tailor = this.querySelector("[data-fit-finder-tailor]");
    this.tailorInput = this.querySelector("[data-fit-finder-tailor-input]");
    this.tailorSize = this.querySelector("[data-fit-finder-tailor-size]");
    this.adjustmentNote = this.querySelector("[data-fit-finder-adjustment]");
    this.adjustmentText = this.querySelector(
      "[data-fit-finder-adjustment-text]",
    );
    this.tailorGap = Number(this.dataset.tailorGap) || 0;
    try {
      this.chart = JSON.parse(
        this.querySelector("[data-fit-finder-chart]").textContent,
      );
      this.productSizes = JSON.parse(
        this.querySelector("[data-fit-finder-sizes]").textContent,
      );
    } catch (error) {
      console.error(error);
      return;
    }
    if (!this.dialog || !this.chart?.length) return;

    this.dialog.addEventListener("input", () => this.update());
    this.dialog.addEventListener("click", (event) => {
      const unitButton = event.target.closest("[data-fit-finder-unit]");
      if (unitButton) {
        this.setUnit(unitButton.dataset.fitFinderUnit, true);
        return;
      }
      // A click on the backdrop lands on the dialog element itself
      if (
        event.target === this.dialog ||
        event.target.closest("[data-fit-finder-close]")
      )
        this.dialog.close();
    });
    // Returning customers get their last measurements and result right away
    const storedUnit = readStorage(UNIT_STORAGE_KEY);
    const unit =
      storedUnit === "metric" || storedUnit === "imperial"
        ? storedUnit
        : this.dataset.defaultUnit || "metric";
    try {
      const stored = JSON.parse(readStorage(MEASUREMENTS_STORAGE_KEY));
      if (stored) this.writeInputs(unit, stored.height, stored.weight);
    } catch (error) {
      // Ignore a malformed entry
    }
    this.setUnit(unit);
    this.selectButton.addEventListener("click", () => this.select());
    this.chartOpen = this.querySelector("[data-fit-finder-chart-open]");
    this.chartView = this.querySelector("[data-fit-finder-chart-view]");
    this.chartContent = this.querySelector("[data-fit-finder-chart-content]");
    this.chartOpen?.addEventListener("click", () => this.showChart(true));
    this.querySelector("[data-fit-finder-chart-back]")?.addEventListener(
      "click",
      () => this.showChart(false),
    );
    this.dialog.addEventListener("close", () => this.showChart(false));
    this.querySelector(
      "[data-fit-finder-adjustment-remove]",
    )?.addEventListener("click", () => this.setAdjustment(null));
    this.onSizeChange = this.onSizeChange.bind(this);
    this.onFormData = this.onFormData.bind(this);
    this.section.addEventListener("change", this.onSizeChange);
    // On document because the sticky form is re-rendered on variant change
    document.addEventListener("formdata", this.onFormData);

    // The theme re-renders the variant picker on every variant change,
    // which drops the link placed in it
    this.observer = new MutationObserver(() => this.renderLink());
    this.observer.observe(this.section, { childList: true, subtree: true });
    this.renderLink();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.section?.removeEventListener("change", this.onSizeChange);
    document.removeEventListener("formdata", this.onFormData);
  }

  get sizeInputs() {
    return Array.from(
      this.section.querySelectorAll(
        `.block-product__variant-picker input.variant-options[data-option-position="${this.dataset.sizePosition}"]`,
      ),
    );
  }

  // The theme keeps its size guide in a template next to the size option
  get chartTemplate() {
    return this.sizeInputs[0]
      ?.closest("fieldset")
      ?.querySelector(".popup-size-chart template");
  }

  showChart(show) {
    if (!this.chartView) return;
    if (show && !this.chartContent.childElementCount) {
      const guide = this.chartTemplate?.content.querySelector(".drawer__body");
      if (!guide) return;
      this.chartContent.innerHTML = guide.innerHTML;
    }
    this.chartView.hidden = !show;
    this.dialog.classList.toggle("fit-finder--chart", show);
    if (show) this.querySelector(".fit-finder__body").scrollTop = 0;
  }

  renderLink() {
    const info = this.sizeInputs[0]
      ?.closest("fieldset")
      ?.querySelector(".product-variants-info");
    if (this.chartOpen) this.chartOpen.hidden = !this.chartTemplate;
    if (!info || info.querySelector("[data-fit-finder-link]")) return;
    const link = document.createElement("button");
    link.type = "button";
    link.className =
      "button btn-reset btn-link border-animation pointer text-size body_weight";
    link.dataset.fitFinderLink = "";
    link.innerHTML = RULER_ICON;
    link.append(this.dataset.linkLabel || "");
    link.addEventListener("click", () => {
      this.dialog.showModal();
      // With a saved result on screen, leave the keyboard closed
      if (!this.result)
        (this.unit === "imperial" ? this.inputs.ft : this.inputs.cm).focus();
    });
    info.classList.add("flex", "wrap", "gap-5", "space-between");
    info.appendChild(link);
  }

  number(input) {
    const value = parseFloat(input.value.replace(",", "."));
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  // Height in cm and weight in kg whatever the unit, or null when not usable
  measurements() {
    let height = this.number(this.inputs.cm);
    let weight = this.number(this.inputs.kg);
    if (this.unit === "imperial") {
      const feet = this.number(this.inputs.ft);
      const inches = this.number(this.inputs.in) || 0;
      const pounds = this.number(this.inputs.lb);
      height = feet === null ? null : (feet * 12 + inches) * CM_PER_INCH;
      weight = pounds === null ? null : pounds * KG_PER_LB;
    }
    const within = (value, [min, max]) =>
      value !== null && value >= min && value <= max ? value : null;
    return {
      height: within(height, HEIGHT_LIMITS),
      weight: within(weight, WEIGHT_LIMITS),
    };
  }

  // Fills the fields of a unit from a height in cm and a weight in kg
  writeInputs(unit, height, weight) {
    const { cm, kg, ft, in: inches, lb } = this.inputs;
    const hasHeight = Number.isFinite(height);
    const hasWeight = Number.isFinite(weight);
    if (unit === "imperial") {
      const totalInches = hasHeight ? Math.round(height / CM_PER_INCH) : null;
      ft.value = totalInches === null ? "" : Math.floor(totalInches / 12);
      inches.value = totalInches === null ? "" : totalInches % 12;
      lb.value = hasWeight ? Math.round(weight / KG_PER_LB) : "";
    } else {
      cm.value = hasHeight ? Math.round(height) : "";
      kg.value = hasWeight ? Math.round(weight) : "";
    }
  }

  // Switches the visible fields, carrying over what was already entered
  setUnit(unit, convert = false) {
    if (unit === this.unit) return;
    if (convert) {
      const { height, weight } = this.measurements();
      this.writeInputs(unit, height, weight);
      writeStorage(UNIT_STORAGE_KEY, unit);
    }
    this.unit = unit;
    this.querySelectorAll("[data-fit-finder-system]").forEach((field) => {
      field.hidden = field.dataset.fitFinderSystem !== unit;
    });
    this.querySelectorAll("[data-fit-finder-unit]").forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.fitFinderUnit === unit),
      );
    });
    this.update();
  }

  // The chart rows a measurement falls in, as the lowest and highest index.
  // Ranges share their boundaries and some overlap, so a value can fit more
  // than one size: 165 cm is both XS (160-165) and S (165-170)
  rowRange(value, key) {
    const last = this.chart.length - 1;
    const min = this.chart[0][key][0];
    const max = this.chart[last][key][1];
    if (value < min)
      return min - value <= TOLERANCE[key].below
        ? { low: 0, high: 0, edge: "below" }
        : { low: 0, high: 0, outside: "below" };
    if (value > max)
      return value - max <= TOLERANCE[key].above
        ? { low: last, high: last, edge: "above" }
        : { low: last, high: last, outside: "above" };
    const fits = (row) => value >= row[key][0] && value <= row[key][1];
    const matches = this.chart.map(fits);
    return { low: matches.indexOf(true), high: matches.lastIndexOf(true) };
  }

  recommend(height, weight) {
    const byHeight = this.rowRange(height, "height");
    const byWeight = this.rowRange(weight, "weight");
    // Outside the chart there is no size to recommend; the customer is
    // pointed to Instagram instead
    if (byHeight.outside === "above" || byWeight.outside === "above")
      return { outside: "above" };
    if (
      byHeight.outside === "below" &&
      (byWeight.outside === "below" || byWeight.low === 0)
    )
      return { outside: "below" };
    // A size that fits both measurements wins, the larger one if there are
    // two, so a value on a boundary is settled by the other measurement.
    // Without a common size, the larger measurement decides so it fits both ways
    const low = Math.max(byHeight.low, byWeight.low);
    const high = Math.min(byHeight.high, byWeight.high);
    const ideal = low <= high ? high : low;
    const nearest = (range) =>
      Math.min(Math.max(ideal, range.low), range.high);
    byHeight.index = nearest(byHeight);
    byWeight.index = nearest(byWeight);
    const available = this.chart
      .map((row, index) => ({ size: row.size, index }))
      .filter((row) => this.productSizes.includes(row.size));
    if (!available.length) return null;
    const match =
      available.find((row) => row.index >= ideal) ||
      available[available.length - 1];
    // Two or more sizes away from anything this item comes in is no better
    // than being outside the chart
    if (match.index - ideal >= 2) return { outside: "below" };
    if (ideal - match.index >= 2) return { outside: "above" };
    const size = match.size;
    const heightSize = this.chart[byHeight.index].size;
    const weightSize = this.chart[byWeight.index].size;
    const idealSize = this.chart[ideal].size;
    const gap = Math.abs(byHeight.index - byWeight.index);

    let note = "";
    if (match.index < ideal) {
      note = `Your measurements point to ${idealSize}, which this item doesn't come in. ${size} is the largest size available and may fit snug.`;
    } else if (match.index > ideal) {
      note = `Your measurements point to ${idealSize}, which this item doesn't come in. ${size} is the smallest size available and may fit loose.`;
    } else if (gap === 1) {
      const smaller = byHeight.index < byWeight.index ? heightSize : weightSize;
      note = `You're between ${smaller} and ${size}. We suggest ${size} for a comfortable fit.`;
    } else if (gap > 1) {
      note = `Your height points to ${heightSize} and your weight to ${weightSize}. We suggest ${size} so it fits comfortably. Message us if you'd like help choosing.`;
    }
    // Just outside the chart, on the size at that end of it
    const last = this.chart.length - 1;
    if (match.index === last && (byHeight.edge === "above" || byWeight.edge === "above")) {
      note = `You're just above our size chart. ${size} is our largest size and may fit a little snug.`;
    } else if (match.index === 0 && byHeight.edge === "below") {
      note = `You're just under our size chart. ${size} is our smallest size and may fit a little loose.`;
    }
    // A size picked for weight is longer than the customer's height needs
    const tailorSize =
      this.tailorGap > 0 &&
      byWeight.index > byHeight.index &&
      match.index - byHeight.index >= this.tailorGap
        ? heightSize
        : null;
    return { size, note, tailorSize, height };
  }

  update() {
    const { height, weight } = this.measurements();
    const result =
      height !== null && weight !== null
        ? this.recommend(height, weight)
        : null;
    const hasSize = !!result?.size;
    this.result = hasSize ? result : null;
    this.emptyText.hidden = !!result;
    this.output.hidden = !hasSize;
    this.selectButton.hidden = !hasSize;
    if (this.outsideBox) {
      this.outsideBox.hidden = !result?.outside;
      this.outsideBox
        .querySelectorAll("[data-fit-finder-outside-text]")
        .forEach((text) => {
          text.hidden = text.dataset.fitFinderOutsideText !== result?.outside;
        });
    }
    if (!result) return;
    writeStorage(MEASUREMENTS_STORAGE_KEY, JSON.stringify({ height, weight }));
    if (!hasSize) return;
    this.sizeText.textContent = result.size;
    this.noteText.textContent = result.note;
    this.noteText.hidden = !result.note;
    this.selectSize.textContent = result.size;
    if (this.tailor) {
      this.tailor.hidden = !result.tailorSize;
      this.tailorSize.textContent = result.tailorSize || "";
    }
  }

  heightText(height) {
    const cm = `${Math.round(height)} cm`;
    if (this.unit !== "imperial") return cm;
    const totalInches = Math.round(height / CM_PER_INCH);
    return `${Math.floor(totalInches / 12)}'${totalInches % 12}" / ${cm}`;
  }

  // The length adjustment travels with the item as a line item property
  // and only applies to the size it was recommended for
  setAdjustment(adjustment) {
    this.adjustment = adjustment;
    if (!this.adjustmentNote) return;
    this.adjustmentNote.hidden = !adjustment;
    this.adjustmentText.textContent = adjustment?.value || "";
  }

  onSizeChange(event) {
    if (!this.adjustment) return;
    const input = event.target;
    if (
      !input.matches?.("input.variant-options[data-option-value]") ||
      input.dataset.optionPosition !== this.dataset.sizePosition
    )
      return;
    if (input.dataset.optionValue !== this.adjustment.size)
      this.setAdjustment(null);
  }

  onFormData(event) {
    const form = event.target;
    // form.id would return the variant input named "id", not the attribute
    if (
      !this.adjustment ||
      (form.getAttribute("id") !== this.dataset.mainFormId &&
        form.getAttribute("id") !== this.dataset.stickyFormId)
    )
      return;
    event.formData.set(
      `properties[${this.dataset.tailorProperty}]`,
      this.adjustment.value,
    );
  }

  select() {
    if (!this.result) return;
    const { size, tailorSize, height } = this.result;
    this.setAdjustment(
      tailorSize && this.tailorInput?.checked
        ? {
            size,
            value: `Shorten to size ${tailorSize} length (height ${this.heightText(height)})`,
          }
        : null,
    );
    const input = this.sizeInputs.find(
      (sizeInput) => sizeInput.dataset.optionValue === this.result.size,
    );
    if (input && !input.checked) input.click();
    this.dialog.close();
  }
}

if (!customElements.get("fit-finder")) {
  customElements.define("fit-finder", FitFinder);
}
